import React, { useEffect, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { handleInputWithLabelOnBlur, handleInputWithLabelOnFocus } from '../../registration';
import InputField from '../../../form/inputField';
import { ELanguage } from '../../utils/language';
import { OrganizationStep2FormResultKeys } from './organizationStep2FormModel';
import { IAccountModel } from '../../../../helpers/models/accountModel';
import { FORM_FIRSTNAME_MAX_LENGTH, FORM_LASTNAME_MAX_LENGTH } from '../../utils/formValidation';

export interface IOrganizationAdministratorProps extends WithTranslation {
    account: IAccountModel;
    readonly?: boolean;
    updateStep?: any;
}

const OrganizationStep2AdministratorProfile = (props: IOrganizationAdministratorProps) => {
    const { t, readonly, updateStep, account, i18n } = props;
    const [language, setLanguage] = useState<boolean>(false);

    useEffect(() => {
        setLanguage((account?.user?.lng ?? i18n.language) === 'en');
    }, [account?.user?.lng, i18n.language]);

    return (
        <>
            <div className="registration_form_header_section registration-form-revise-submit--modify-section">
                <h1 className="header-3 header-3--purple">{t('form_organization_step_2_title')}</h1>
                {readonly && (
                    <a
                        data-step={2}
                        className="standalone-link registration_form--modify-button"
                        tabIndex={0}
                        role="button"
                        onClick={updateStep}
                        aria-label={t('form_organization_step_2_title')
                            .addSpace()
                            .concat(t('form_revise_submit_organization_step_2_modify_button'))}
                    >
                        {t('form_revise_submit_organization_step_2_modify_button')}
                        <span className="registration-from--modify-button-icon"></span>
                    </a>
                )}
            </div>
            <div className="registration-form-container">
                <div className="registration-section">
                    <div className="registration-column-container">
                        <InputField
                            fieldContainerClassName="registration-column-item"
                            fieldName={OrganizationStep2FormResultKeys.FIRST_NAME}
                            fieldTitle="form_organization_step_2_first_name"
                            fieldDisabled={readonly}
                            fieldRequired
                            type="text"
                            defaultValue={account.firstname ?? ''}
                            fieldWeight={0}
                            maxLength={FORM_FIRSTNAME_MAX_LENGTH}
                            fieldHiddenLabel="form_organization_step_2_title"
                        />
                        <InputField
                            fieldContainerClassName="registration-column-item"
                            fieldName={OrganizationStep2FormResultKeys.LAST_NAME}
                            fieldTitle="form_organization_step_2_last_name"
                            fieldDisabled={readonly}
                            fieldRequired
                            type="text"
                            defaultValue={account.lastname ?? ''}
                            fieldWeight={1}
                            maxLength={FORM_LASTNAME_MAX_LENGTH}
                            fieldHiddenLabel="form_organization_step_2_title"
                        />
                    </div>
                </div>
                <div className="registration-section">
                    <div className="registration-column-container registration-preferred-lang-container">
                        <div className="body-medium">
                            {t('form_organization_step_2_correspondence_lang_title')}
                        </div>
                        <div className="registration-preferred-lang-selection">
                            <div className="radio-button-container">
                                <input
                                    className="radio-button--input field-value"
                                    type="radio"
                                    value={ELanguage.English}
                                    id="form_organization_step_2_correspondence_lang_english"
                                    name={OrganizationStep2FormResultKeys.LNG}
                                    onBlur={handleInputWithLabelOnBlur}
                                    onFocus={handleInputWithLabelOnFocus}
                                    disabled={readonly}
                                    checked={language}
                                    onChange={(e) => {
                                        e.target.checked && setLanguage(true);
                                    }}
                                />
                                <label
                                    htmlFor="form_organization_step_2_correspondence_lang_english"
                                    className={'radio-button radio-button--no-margin field-label registration-column-item '.concat(
                                        readonly ? 'field-label--disabled' : ''
                                    )}
                                    aria-label={
                                        t('form_organization_step_2_correspondence_lang_title') +
                                        ' ' +
                                        t('form_organization_step_2_correspondence_lang_english')
                                    }
                                >
                                    <span className="accessibility-hidden">
                                        {t('form_organization_step_2_correspondence_lang_title')}
                                    </span>
                                    {t('form_organization_step_2_correspondence_lang_english')}
                                </label>
                            </div>
                            <div className="radio-button-container">
                                <input
                                    className="radio-button--input field-value"
                                    type="radio"
                                    value={ELanguage.French}
                                    id="form_organization_step_2_correspondence_lang_french"
                                    name={OrganizationStep2FormResultKeys.LNG}
                                    onBlur={handleInputWithLabelOnBlur}
                                    onFocus={handleInputWithLabelOnFocus}
                                    disabled={readonly}
                                    checked={!language}
                                    onChange={(e) => {
                                        e.target.checked && setLanguage(false);
                                    }}
                                />
                                <label
                                    htmlFor="form_organization_step_2_correspondence_lang_french"
                                    className={'radio-button radio-button--no-margin field-label registration-column-item '.concat(
                                        readonly ? 'field-label--disabled' : ''
                                    )}
                                    aria-label={
                                        t('form_organization_step_2_correspondence_lang_title') +
                                        ' ' +
                                        t('form_organization_step_2_correspondence_lang_french')
                                    }
                                >
                                    <span className="accessibility-hidden">
                                        {t('form_organization_step_2_correspondence_lang_title')}
                                    </span>
                                    {t('form_organization_step_2_correspondence_lang_french')}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default withTranslation()(OrganizationStep2AdministratorProfile);
